import React, { useState, useEffect } from 'react';
import { useModal } from '../contexts/ModalContext';
import { useAuth } from '../contexts/AuthContext';

function ForgotPasswordModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const { setModalOpen } = useModal();
  const { checkEmailForPasswordReset } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setModalOpen(true);
    } else {
      setIsAnimating(false);
      setModalOpen(false);
      // Limpa os campos quando o modal fecha
      setEmail('');
      setError('');
      setSuccess(false);
      setIsGoogleAccount(false);
    }
  }, [isOpen, setModalOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await checkEmailForPasswordReset(email);
      
      if (!result.exists) {
        setError('Email não encontrado em nossa base de dados');
      } else if (result.isGoogleAccount) {
        setError('Esta conta foi criada com Google. Use o botão "Entrar com Google" para fazer login.');
        setIsGoogleAccount(true);
      } else if (result.emailSent) {
        // Email existe e foi enviado com sucesso
        setSuccess(true);
      }
    } catch (error) {
      console.error('Erro ao verificar email:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={() => onClose()}
    >
      {/* Overlay escuro */}
      <div 
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-all duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Modal */}
      <div 
        className={`
          bg-white w-full h-full relative z-50 rounded-lg
          sm:rounded-2xl sm:max-w-md sm:mx-4 sm:h-auto
          flex flex-col
          transition-all duration-200
          ${isOpen 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
          }
        `}
        onClick={e => e.stopPropagation()}
      >
        {/* Fixed Header */}
        <div className="rounded-t-lg flex justify-between items-center p-6 pb-4 pt-4 bg-white border-b border-gray-200 sticky top-0 z-10">
          <h2 className="text-2xl font-bold text-gray-900">Esqueci minha senha</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 pt-4">
          {success ? (
            <div className="text-center space-y-6">
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <p className="text-sm font-medium">
                  Email enviado com sucesso!
                </p>
                <p className="text-xs mt-1">
                  Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                </p>
              </div>
              
              <div className="text-sm text-gray-600">
                <p>Não recebeu o email? Verifique sua pasta de spam ou tente novamente.</p>
              </div>

              <button
                onClick={onClose}
                className="w-full py-3 px-4 text-white rounded-lg font-medium text-sm transition-colors"
                style={{
                  backgroundColor: 'var(--primary-color)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-color-hover)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-color)';
                }}
              >
                Fechar
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm">
                  Digite seu email e enviaremos um link para redefinir sua senha.
                </p>
              </div>

              {error && (
                <div className={`px-4 py-3 rounded-lg mb-6 ${
                  isGoogleAccount 
                    ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' 
                    : 'bg-red-50 border border-red-200 text-red-600'
                }`}>
                  <div className="flex items-start">
                    {isGoogleAccount && (
                      <svg className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    )}
                    <div>
                      <p className="text-sm font-medium">{error}</p>
                      {isGoogleAccount && (
                        <p className="text-xs mt-1">
                          Contas criadas com Google não possuem senha para redefinir.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                      placeholder="Seu email"
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 px-4 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: 'var(--primary-color)',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-color-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-color)';
                    }}
                  >
                    {isLoading ? 'Enviando...' : 'Enviar link de redefinição'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordModal;
